/**
 * @file     useStickyBarVisibility.ts
 * @purpose  True once the hero's rail actions have scrolled up out of view,
 *           so the top bar and phone bar take over only when those buttons are
 *           gone (one set of actions on screen at a time). IntersectionObserver
 *           on the actions element, with the glass header's height as the top
 *           margin, plus a per-frame rect check for jumps the observer cannot
 *           see; never a scroll-distance threshold, so late layout shifts
 *           cannot raise the bar. Also reports whether the site footer is in
 *           view, so the phone bar can step aside for the board disclaimer.
 */
'use client';

import { useEffect, useState } from 'react';

/** The glass header's bottom edge (Navbar publishes it); content scrolls under it. */
function navHeightPx(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--nav-height');
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

export function useStickyBarVisibility(anchorId: string): { visible: boolean; footerInView: boolean } {
  const [visible, setVisible] = useState(false);
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const anchor = document.getElementById(anchorId);
    if (!anchor) return;
    const inset = Math.round(navHeightPx());
    const passed = (bottom: number, top: number) => bottom <= top + 1;
    const anchorObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        // Not intersecting AND above the top edge: the visitor scrolled past
        // it. Below the viewport (page not scrolled yet) keeps the bar hidden.
        const top = entry.rootBounds?.top ?? inset;
        setVisible(!entry.isIntersecting && passed(entry.boundingClientRect.bottom, top));
      },
      { rootMargin: `-${inset}px 0px 0px 0px` },
    );
    anchorObserver.observe(anchor);

    // An anchor jump or a fast fling can carry the actions from below the
    // viewport to above it with no intersection in between, so the observer
    // never fires. One rect read per frame while scrolling settles it.
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setVisible(passed(anchor.getBoundingClientRect().bottom, inset));
      });
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    const footer = document.querySelector('footer');
    const footerObserver = footer
      ? new IntersectionObserver(([entry]) => {
          if (entry) setFooterInView(entry.isIntersecting);
        })
      : null;
    if (footer) footerObserver?.observe(footer);

    return () => {
      anchorObserver.disconnect();
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (frame) window.cancelAnimationFrame(frame);
      footerObserver?.disconnect();
    };
  }, [anchorId]);

  return { visible, footerInView };
}
